import { PointsPrice } from "@/components/organisms/SBCs/PointsPrice/PointsPrice";
import { NewTag } from "@/design-system/Tags/NewTag/NewTag";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { useMediaQuery } from "usehooks-ts";

import { convertSBCPrice } from "../../../../functions/convertPlayerPrice";
import { Platform } from "../../../../redux/user/userSlice";

type Props = {
  pcPrice: number;
  psPrice: number;
  pointsRequired?: number;
  remainingCost: number | null;
  platform: Platform;
  isNew: boolean;
  hasRefreshed: boolean;
};

/**
 * What the set costs, under its name. The figure is the second thing read on
 * the page, so it is set at heading size in the number face.
 */
export const SetHeaderSubtitle = ({
  pcPrice,
  psPrice,
  pointsRequired,
  remainingCost,
  platform,
  isNew,
  hasRefreshed,
}: Props) => {
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const { t } = useTranslation(["sbcs", "common"]);

  return (
    <div className="flex flex-col gap-x-3 mobile:flex-row mobile:items-baseline">
      <div className="flex flex-row items-center gap-x-2">
        <img
          className="size-4 desktop:size-6"
          src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/misc/coin-tmp.png`}
          alt={t("common:alt.coin")}
        />
        <p className="font-oswald text-lg font-medium leading-none text-foreground desktop:text-2xl">
          {convertSBCPrice(platform === "PC" ? pcPrice : psPrice)}
        </p>
        {pointsRequired != null && (
          <PointsPrice
            points={pointsRequired}
            size={16}
            className="text-sm text-foreground-muted"
          />
        )}
        {(isNew || hasRefreshed) && !isTouchDevice && (
          <NewTag
            tag={isNew ? t("common:tags.new") : t("common:tags.refreshed")}
          />
        )}
      </div>
      {/* Desktop only: on a phone the row is already the price, the tags and
          the vote beside them. Always rendered, hidden rather than dropped,
          so a remaining cost that arrives with the club does not shift the
          rows under it. */}
      {!isTouchDevice && (
        <span
          className={cn(
            "text-sm text-foreground-muted",
            !(remainingCost > 0) && "invisible",
          )}
        >
          {t("sbcs:set.remaining", {
            price: convertSBCPrice(remainingCost || 0),
          })}
        </span>
      )}
    </div>
  );
};
